import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../api/apiService';

// Initial state
const initialState = {
  priceList: [],
  loading: false,
  error: null,
  categories: [],
};

// Async thunks
export const fetchPriceList = createAsyncThunk(
  'priceList/fetchPriceList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getPriceList();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch price list');
    }
  }
);

export const createPriceItem = createAsyncThunk(
  'priceList/createPriceItem',
  async (itemData, { rejectWithValue }) => {
    try {
      const response = await apiService.createPriceItem(itemData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create price item');
    }
  }
);

export const updatePriceItem = createAsyncThunk(
  'priceList/updatePriceItem',
  async ({ id, itemData }, { rejectWithValue }) => {
    try {
      const response = await apiService.updatePriceItem(id, itemData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update price item');
    }
  }
);

export const deletePriceItem = createAsyncThunk(
  'priceList/deletePriceItem',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deletePriceItem(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete price item');
    }
  }
);

// Price list slice
const priceListSlice = createSlice({
  name: 'priceList',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateCategories: (state) => {
      state.categories = [...new Set(state.priceList.map(item => item.category))];
    },
    // Fallback to localStorage if API fails
    loadFromLocalStorage: (state) => {
      const savedPriceList = localStorage.getItem('priceList');
      if (savedPriceList) {
        state.priceList = JSON.parse(savedPriceList);
        state.categories = [...new Set(state.priceList.map(item => item.category))];
      }
    },
    saveToLocalStorage: (state) => {
      localStorage.setItem('priceList', JSON.stringify(state.priceList));
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch price list
      .addCase(fetchPriceList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPriceList.fulfilled, (state, action) => {
        state.loading = false;
        state.priceList = action.payload;
        state.categories = [...new Set(action.payload.map(item => item.category))];
      })
      .addCase(fetchPriceList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Fallback to localStorage
        const savedPriceList = localStorage.getItem('priceList');
        if (savedPriceList) {
          state.priceList = JSON.parse(savedPriceList);
          state.categories = [...new Set(state.priceList.map(item => item.category))];
        }
      })
      
      // Create price item
      .addCase(createPriceItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPriceItem.fulfilled, (state, action) => {
        state.loading = false;
        state.priceList.push(action.payload);
        state.categories = [...new Set(state.priceList.map(item => item.category))];
        // Save to localStorage as backup
        localStorage.setItem('priceList', JSON.stringify(state.priceList));
      })
      .addCase(createPriceItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update price item
      .addCase(updatePriceItem.fulfilled, (state, action) => {
        const index = state.priceList.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.priceList[index] = action.payload;
          state.categories = [...new Set(state.priceList.map(item => item.category))];
          // Save to localStorage as backup
          localStorage.setItem('priceList', JSON.stringify(state.priceList));
        }
      })
      
      // Delete price item
      .addCase(deletePriceItem.fulfilled, (state, action) => {
        state.priceList = state.priceList.filter(item => item.id !== action.payload);
        state.categories = [...new Set(state.priceList.map(item => item.category))];
        // Save to localStorage as backup
        localStorage.setItem('priceList', JSON.stringify(state.priceList));
      });
  },
});

export const { clearError, updateCategories, loadFromLocalStorage, saveToLocalStorage } = priceListSlice.actions;
export default priceListSlice.reducer;
